import { useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { useLocalisation } from '../../../../services/contextAdaptor/LocalisationContext';
import { logWarnWrapper } from '../../../../services/logWrapper';
import {
  getDestinationCityName,
  getDestinationId,
  getOriginId,
  getSelectedInboundDate,
  getSelectedOutboundDate,
  getStay,
  getTripDays,
} from '../../selectors';
import {
  getMichelinRecommendation,
  getMichelinRecommendationsStatus,
  getMichelinHotelContent,
  getMichelinHotelContentStatus,
} from '../../selectors/michelinSelectors';
import { COMPONENT_CAPYBARA_RECOMMENDATIONS } from '../constants/analyticsConstants';
import { buildComboRecommendation } from '../data-parsing/recommendationMapper';

import type { GetResultUrl } from '../../../../services/contextAdaptor/types';
import type { ComboRecommendation } from '../../components/RecommendedForYou/types';

type ComboRecommendationResult = {
  status: ReturnType<typeof getMichelinRecommendationsStatus>;
  comboRecommendation: ComboRecommendation | undefined;
};

export const useComboRecommendation = (
  getResultUrl: GetResultUrl,
): ComboRecommendationResult => {
  const { formatCurrency, formatDateAbbr, formatNumber } = useLocalisation();
  const status = useSelector(getMichelinRecommendationsStatus);
  const recommendation = useSelector(getMichelinRecommendation);
  const hotelContent = useSelector(getMichelinHotelContent);
  const hotelContentStatus = useSelector(getMichelinHotelContentStatus);
  const destination = useSelector(getDestinationCityName);
  const destinationId = useSelector(getDestinationId);
  const originId = useSelector(getOriginId);
  const outboundDate = useSelector(getSelectedOutboundDate);
  const inboundDate = useSelector(getSelectedInboundDate);
  const stay = useSelector(getStay);
  const tripDays = useSelector(getTripDays);

  const impressionId = useMemo(() => uuid(), []);

  const hotelId =
    recommendation?.verticalResults?.find((v) => v.hotelResult)?.hotelResult
      ?.hotelId || 'unknown';

  useEffect(() => {
    if (recommendation && !hotelContent && hotelContentStatus === 'error') {
      logWarnWrapper({
        message: 'Hotel content unavailable for combo recommendation',
        component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
        hasRecommendation: '1',
        hotelId,
      });
    }
  }, [hotelContent, hotelContentStatus, hotelId, recommendation]);

  const comboRecommendation = useMemo(() => {
    if (!recommendation || !outboundDate || !hotelContent) return undefined;

    return (
      buildComboRecommendation({
        recommendation,
        destination,
        originId,
        destinationId,
        outboundDate,
        inboundDate,
        localisation: { formatCurrency, formatDateAbbr, formatNumber },
        hotelContent,
        stay,
        impressionId,
        getResultUrl,
        tripDays,
      }) ?? undefined
    );
  }, [
    recommendation,
    outboundDate,
    hotelContent,
    destination,
    originId,
    destinationId,
    inboundDate,
    formatCurrency,
    formatDateAbbr,
    formatNumber,
    stay,
    impressionId,
    getResultUrl,
    tripDays,
  ]);

  return { status, comboRecommendation };
};
